
import React, { useEffect, useState } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';

interface AdUnitProps {
  adSlot: string;
  adFormat?: 'auto' | 'rectangle' | 'horizontal';
  className?: string;
  mobileOnly?: boolean;
  desktopOnly?: boolean;
}

declare global {
  interface Window {
    adsbygoogle: any[];
  }
}

export function AdUnit({ 
  adSlot, 
  adFormat = 'auto', 
  className = '',
  mobileOnly = false,
  desktopOnly = false
}: AdUnitProps) {
  const [adError, setAdError] = useState(false);
  const isMobile = useIsMobile();
  
  useEffect(() => {
    // Skip ad initialization if this component shouldn't be shown on this device
    if ((mobileOnly && !isMobile) || (desktopOnly && isMobile)) {
      return;
    }
    
    const initAd = async () => {
      try {
        // Check if we're in development
        if (process.env.NODE_ENV === 'development') {
          console.log('AdSense ads are disabled in development environment');
          return;
        }

        if (window.adsbygoogle) {
          await (window.adsbygoogle = window.adsbygoogle || []).push({});
          console.log('AdSense ad initialized successfully');
        } else {
          console.warn('AdSense not available on window object');
        }
      } catch (error) {
        console.error('Error loading AdSense ad:', error);
        setAdError(true);
      }
    };

    // Short delay to ensure the ad container is fully rendered
    const timer = setTimeout(() => {
      initAd();
    }, 100);

    return () => clearTimeout(timer);
  }, [isMobile, mobileOnly, desktopOnly]);

  // Check if we should render based on device type
  if ((mobileOnly && !isMobile) || (desktopOnly && isMobile)) {
    return null;
  }

  // Don't render anything if there's an error or we're in development
  if (adError || process.env.NODE_ENV === 'development') {
    return (
      <div className={`${className} flex items-center justify-center`}>
        <p className="text-gray-500 text-sm">{process.env.NODE_ENV === 'development' ? 'Ad disabled in development' : 'Advertisement'}</p>
      </div>
    );
  }

  return (
    <ins
      className={`adsbygoogle ${className}`}
      style={{ display: 'block' }}
      data-ad-client="ca-pub-5580992564457281"
      data-ad-slot={adSlot}
      data-ad-format={adFormat}
      data-full-width-responsive="true"
    />
  );
}
